import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qs, type SearchTuning } from '../lib/api'
import { Empty, ErrorNote, Toggle } from '../components/Page'
import { Card, ConsoleHead, SliderRow } from '../components/admin/Console'

/** What a trial query returns. The shape is loose because rank is the only field that matters here. */
interface TryRow {
  id?: string
  unit_type?: string
  title?: string | null
  headline?: string | null
  pub_date?: string | null
  section_label?: string | null
  page_number?: number | null
  rank: number
  matched_layers?: string[]
}

interface TryResponse {
  query?: string
  elapsed_ms?: number
  results?: TryRow[]
}

/**
 * Search tuning.
 *
 * Every relative weight on this screen depends on measured behaviour rather
 * than on a guess: `ts_rank_cd` returns 0.4 to 1.6 on this corpus, and the
 * semantic floors are query-to-article numbers on a different scale from
 * sentence-to-sentence similarity. The trial query is here so a change is
 * checked against the corpus before it is saved rather than after.
 */
export function SearchTuningPage() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<SearchTuning | null>(null)
  const [query, setQuery] = useState('')
  const [asked, setAsked] = useState('')

  const tuning = useQuery({
    queryKey: ['admin', 'search-tuning'],
    queryFn: () => api.get<SearchTuning>('/admin/search-tuning'),
    retry: false,
  })

  useEffect(() => {
    if (tuning.data) setDraft(structuredClone(tuning.data))
  }, [tuning.data])

  const save = useMutation({
    mutationFn: (body: SearchTuning) => api.put<SearchTuning>('/admin/search-tuning', body),
    onSuccess: (next) => {
      queryClient.setQueryData(['admin', 'search-tuning'], next)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'search-tuning', 'try'] })
    },
  })

  const trial = useQuery({
    queryKey: ['admin', 'search-tuning', 'try', asked],
    queryFn: () => api.get<TryResponse | TryRow[]>(`/admin/search-tuning/try${qs({ q: asked })}`),
    enabled: asked.length > 0,
    retry: false,
  })

  if (tuning.isLoading) {
    return (
      <>
        <ConsoleHead title="Search tuning" />
        <Card>
          <Empty>Reading the current weights.</Empty>
        </Card>
      </>
    )
  }

  if (tuning.isError || !draft) {
    return (
      <>
        <ConsoleHead title="Search tuning" />
        <Card>
          <Empty>
            The tuning endpoint is not in this build yet. The blend weights live in{' '}
            <span className="font-mono text-[13px]">app_settings</span> under the key{' '}
            <span className="font-mono text-[13px]">search.weights</span>, and this screen reads and
            writes them at <span className="font-mono text-[13px]">/api/admin/search-tuning</span>{' '}
            once that route exists. Until then the weights are whatever the search service defaults
            to.
          </Empty>
        </Card>
        <ErrorNote error={tuning.error} />
      </>
    )
  }

  const rows = Array.isArray(trial.data) ? trial.data : (trial.data?.results ?? [])
  const dirty = JSON.stringify(draft) !== JSON.stringify(tuning.data)

  return (
    <>
      <ConsoleHead
        title="Search tuning"
        note={dirty ? 'unsaved changes' : 'saved'}
        actions={
          <button
            type="button"
            className="btn-primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {save.isPending ? 'Saving…' : 'Save weights'}
          </button>
        }
      />

      <ErrorNote error={save.error} />

      <Card className="grid gap-px bg-n300 lg:grid-cols-[1.1fr_1fr_1fr]">
        <Column title="Ranking weights">
          {Object.entries(draft.weights).map(([key, value]) => (
            <SliderRow
              key={key}
              label={key.replace(/_/g, ' ')}
              value={value}
              min={0}
              max={4}
              step={0.1}
              onChange={(next) =>
                setDraft({ ...draft, weights: { ...draft.weights, [key]: next } })
              }
            />
          ))}
        </Column>

        <Column title="Fuzzy matching">
          <SliderRow
            label="trigram threshold"
            value={draft.fuzzy.trigram_threshold}
            min={0.1}
            max={0.9}
            step={0.01}
            onChange={(next) =>
              setDraft({ ...draft, fuzzy: { ...draft.fuzzy, trigram_threshold: next } })
            }
          />
          <SliderRow
            label="max edit distance"
            value={draft.fuzzy.max_edit_distance}
            min={0}
            max={4}
            step={1}
            onChange={(next) =>
              setDraft({ ...draft, fuzzy: { ...draft.fuzzy, max_edit_distance: next } })
            }
          />
          <div className="flex items-center gap-[11px]">
            <div className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-ink2">
              phonetic fallback
            </div>
            <Toggle
              on={draft.fuzzy.phonetic_fallback}
              label="Phonetic fallback"
              onChange={(next) =>
                setDraft({ ...draft, fuzzy: { ...draft.fuzzy, phonetic_fallback: next } })
              }
            />
            <div className="w-[46px] flex-none text-right font-mono text-[12.5px]">
              {draft.fuzzy.phonetic_fallback ? 'on' : 'off'}
            </div>
          </div>
          <SliderRow
            label="min token length"
            value={draft.fuzzy.min_token_length}
            min={2}
            max={8}
            step={1}
            onChange={(next) =>
              setDraft({ ...draft, fuzzy: { ...draft.fuzzy, min_token_length: next } })
            }
          />
        </Column>

        <Column title="Semantic">
          <SliderRow
            label="k nearest"
            value={draft.semantic.k}
            min={10}
            max={500}
            step={10}
            onChange={(next) => setDraft({ ...draft, semantic: { ...draft.semantic, k: next } })}
          />
          <SliderRow
            label="min cosine"
            value={draft.semantic.min_cosine}
            min={0}
            max={1}
            step={0.01}
            onChange={(next) =>
              setDraft({ ...draft, semantic: { ...draft.semantic, min_cosine: next } })
            }
          />
          <SliderRow
            label="blend with exact"
            value={draft.semantic.blend}
            min={0}
            max={1}
            step={0.05}
            onChange={(next) =>
              setDraft({ ...draft, semantic: { ...draft.semantic, blend: next } })
            }
          />
          <p className="mt-1 font-mono text-[9.5px] leading-[1.7] text-ink2">
            THE SEMANTIC FLOORS ARE QUERY-TO-ARTICLE NUMBERS. BACKGROUND SIMILARITY IS 0.05 TO 0.11
            ON THIS CORPUS; RELEVANT IS 0.34 TO 0.55.
          </p>
        </Column>
      </Card>

      <div className="mt-6 max-w-[720px]">
        <ConsoleHead title="Try a query" note={rows.length ? `${rows.length} results` : undefined} />
        <Card>
          <form
            className="flex items-center gap-2 border-b border-n300 px-[15px] py-3"
            onSubmit={(event) => {
              event.preventDefault()
              setAsked(query.trim())
            }}
          >
            <input
              className="input-dense"
              placeholder="A query to run against the saved weights"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Trial query"
            />
            <button type="submit" className="btn-secondary flex-none" disabled={!query.trim()}>
              Run
            </button>
          </form>

          {!asked ? (
            <Empty>
              Type a query to see the top ten it returns under the weights above. A weight is worth
              changing only when you can see what it moves.
            </Empty>
          ) : trial.isLoading ? (
            <Empty>Running the query.</Empty>
          ) : trial.isError ? (
            <Empty>
              The trial endpoint is not in this build yet. It answers at{' '}
              <span className="font-mono text-[13px]">/api/admin/search-tuning/try?q=</span> with the
              top ten under the current weights.
            </Empty>
          ) : !rows.length ? (
            <Empty>Nothing in the corpus matches that query under the current weights.</Empty>
          ) : (
            <ol>
              {rows.slice(0, 10).map((row, index) => (
                <li
                  key={row.id ?? index}
                  className="flex items-baseline gap-3 border-b border-n200 px-[15px] py-2 last:border-b-0"
                >
                  <span className="w-4 flex-none font-mono text-[10px] text-ink2">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-[14px]">
                    {row.title ?? row.headline ?? 'Untitled'}
                  </span>
                  <span className="flex-none font-mono text-[10px] text-ink2">
                    {[row.unit_type, row.pub_date, row.section_label].filter(Boolean).join(' · ')}
                  </span>
                  <span className="w-[46px] flex-none text-right font-mono text-[11.5px]">
                    {row.rank?.toFixed(3)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </>
  )
}

function Column({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-paper px-[17px] pb-[17px] pt-[15px]">
      <div className="chrome mb-3 text-[11px]">{title}</div>
      <div className="flex flex-col gap-2.5">{children}</div>
    </div>
  )
}
